'use client'

import { useRouter } from '@/navigation'
import { useForm } from '@/shared/hooks/useForm'
import { resolvePath } from '@/shared/utils/routesUtils'
import Banner from '@/shared/v2/shared/components/Banner'
import Button from '@/shared/v2/shared/components/Button'
import Dialog from '@/shared/v2/shared/components/Dialog'
import Input from '@/shared/v2/shared/components/Input'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import { Textarea } from '@hello-aria/ui/textarea'
import { Field } from '@hello-aria/ui/field'
import { toControlProps } from '@/shared/utils/inputUtils'
import Title from '@/shared/v2/shared/components/Title'
import { trackEvent } from '@/shared/utils/analyticsUtils'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { z } from 'zod'

const UserFeedbackSchema = z.object({
    email: z.string().email().min(1),
    message: z.string().min(1)
})

type UserFeedback = z.infer<typeof UserFeedbackSchema>

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
    const t = useTranslations('root')
    const router = useRouter()

    const [isOpen, setIsOpen] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const form = useForm({
        schema: UserFeedbackSchema
    })

    const messageField = form.register('message')

    const onBackHome = () => {
        router.push(resolvePath('/'))
    }

    const onReload = () => {
        reset()
    }

    const onReport = (data: UserFeedback) => {
        try {
            trackEvent('Report_Error', {
                email: data.email,
                message: data.message,
                digest: error.digest ?? 'none'
            })
            setIsSubmitted(true)
            setIsOpen(false)
            form.reset()
        } catch (reportError) {
            console.error('Failed to report the error:', reportError)
        }
    }

    const onOpenChange = (open: boolean) => {
        setIsOpen(open)

        if (!open) {
            form.reset()
        }
    }
    return (
        <div className="flex flex-col text-center items-center justify-center w-full h-full gap-8">
            <div>
                <Title.Lg>{t('globalError.title')}</Title.Lg>
                <Paragraph>{t('globalError.description')}</Paragraph>
            </div>

            <div className="flex items-center justify-center gap-2">
                <Button intent="secondary" onClick={onBackHome}>
                    {t('globalError.actions.home')}
                </Button>
                <Button intent="secondary" onClick={onReload}>
                    {t('globalError.actions.reload')}
                </Button>

                <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
                    <Dialog.Trigger asChild>
                        <Button>{t('globalError.actions.report')}</Button>
                    </Dialog.Trigger>
                    <Dialog.Content title={t('globalError.actions.report')} className="max-w-175">
                        <Dialog.Description className="sr-only">
                            {t('globalError.reportDescription')}
                        </Dialog.Description>
                        <form onSubmit={form.handleSubmit(onReport)}>
                            <Dialog.Body className="flex flex-col gap-4">
                                <Input
                                    label={t('globalError.form.email.label')}
                                    placeholder={t('globalError.form.email.placeholder')}
                                    {...form.register('email')}
                                />
                                <Field label={t('globalError.form.message.label')} error={messageField.message}>
                                    <Textarea
                                        placeholder={t('globalError.form.message.placeholder')}
                                        {...toControlProps(messageField)}
                                    />
                                </Field>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Dialog.Close asChild>
                                    <Button intent={'secondary'} type={'button'}>
                                        {t('globalError.actions.cancel')}
                                    </Button>
                                </Dialog.Close>

                                <Button data-testid="__error-report-submit__" intent="primary" type="submit">
                                    {t('globalError.actions.report')}
                                </Button>
                            </Dialog.Footer>
                        </form>
                    </Dialog.Content>
                </Dialog.Root>
            </div>
            {isSubmitted ? (
                <Banner className="max-w-100" title={t('globalError.form.submitted')} intent="positive" />
            ) : null}
        </div>
    )
}
